import type { ParsedSlip } from '@porttang/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/useAuth';
import { firestore, functions } from '../../shared/firebase/client';
import { encodeImageForUpload } from '../../shared/image';
import { holdingsQueryKey } from '../holdings/useHoldings';

/** ตรงกับ `ScanSlipResult` ฝั่ง function */
export interface ScanSlipResult {
  slip: ParsedSlip;
  outOfScope: string | null;
  slipHash: string | null;
  appliedAt: string | null;
}

const callScanSlip = httpsCallable<{ imageBase64: string; mimeType: string }, ScanSlipResult>(
  functions,
  'scanSlip',
);

/**
 * ส่งรูปสลิปไปให้อ่าน แล้วได้ร่างกลับมา — **ไม่แตะพอร์ตเลย**
 *
 * ไม่ invalidate cache ใด ๆ เพราะยังไม่มีอะไรถูกบันทึก การเขียนจริงเกิดตอนผู้ใช้
 * กดยืนยันบนจอทบทวน ผ่าน mutation ของ holdings ที่มีอยู่แล้ว
 */
export function useScanSlip() {
  return useMutation({
    mutationFn: async (file: File): Promise<ScanSlipResult> => {
      const encoded = await encodeImageForUpload(file);
      const response = await callScanSlip({
        imageBase64: encoded.base64,
        mimeType: encoded.mimeType,
      });
      return response.data;
    },
    // สลิปใบเดิมยิงซ้ำก็ได้ผลเหมือนเดิม และแต่ละครั้งกินโควตา Vision จริง ๆ
    retry: false,
  });
}

/**
 * จดว่าสลิปใบนี้ถูกบันทึกเข้าพอร์ตแล้ว — `users/{uid}/slipMarks/{slipHash}`
 *
 * ## นี่ไม่ใช่ transaction ledger
 *
 * เก็บแค่ "เคยผ่านมาแล้ว" กับเวลาที่ผ่าน ไม่มีจำนวน ไม่มีราคา ไม่มีรูป — อ่านย้อนกลับ
 * มาคำนวณอะไรไม่ได้เลย เพราะ id เป็น hash ของลายนิ้วมือ ไม่ใช่ตัวลายนิ้วมือ
 * หน้าที่เดียวคือตอบว่าสลิปใบนี้เคยถูกบันทึกไปแล้วหรือยัง (spec Section 1 ยังคงอยู่ครบ)
 *
 * ## ทำไมเขียนตอนกดยืนยัน ไม่ใช่ตอนสแกน
 *
 * สแกนแล้วปิดทิ้งเป็นเรื่องปกติ (ถ่ายเบลอ, เลือกไฟล์ผิด) ถ้าจดตั้งแต่ตอนสแกน
 * ใบที่ยังไม่เคยบันทึกจะถูกเตือนว่าซ้ำในครั้งถัดไป แล้วผู้ใช้จะเรียนรู้ที่จะกดข้ามคำเตือน
 */
export function useMarkSlipApplied() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slipHash: string) => {
      if (user === null) {
        throw new Error('ต้องเข้าสู่ระบบก่อน');
      }
      await setDoc(doc(firestore, 'users', user.uid, 'slipMarks', slipHash), {
        appliedAt: serverTimestamp(),
      });
    },
    onSettled: () => {
      if (user !== null) {
        void queryClient.invalidateQueries({ queryKey: holdingsQueryKey(user.uid) });
      }
    },
  });
}
